import { Fragment, useCallback, useMemo } from "react"
import { ChartTooltip, Swatch } from "@/components/chart/ChartTooltip"
import { useChartTheme } from "@/lib/chart/ChartThemeContext"
import { formatNumber, formatTimeLabel, withUnit } from "@/lib/chart/format"
import { cn } from "@/lib/cn"
import { renderSeriesChart, seriesTimeline, seriesXRatios } from "@/lib/chart/renderSeries"
import { visibleBarCount, type ChartRange } from "@/lib/chart/timeframes"
import {
  CHART_NAVIGATION_HINT,
  useCanvasFrame,
  useCanvasSize,
  useChartViewport,
  type ChartViewport,
} from "@/hooks/useChartViewport"
import type { SeriesChartData, SeriesDefinition } from "@/types/seriesChart"

interface Props {
  data: SeriesChartData
  visibleKeys: string[]
  range: ChartRange
}

export function SeriesChartSurface({ data, visibleKeys, range }: Props) {
  const theme = useChartTheme()
  const timeline = useMemo(() => seriesTimeline(data, visibleKeys), [data, visibleKeys])
  const defaultWindow = useMemo(
    () => visibleBarCount(timeline.map((time) => ({ time })), range),
    [range, timeline],
  )
  const resetKey = useMemo(() => ({}), [data, range])
  const pointRatios = useCallback(
    (viewport: ChartViewport) => seriesXRatios(timeline.slice(viewport.start, viewport.end), data.x_scale),
    [data.x_scale, timeline],
  )
  const {
    containerRef,
    canvasRef,
    size,
    viewport,
    hoverIndex,
    hoverX,
    hoverY,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerLeave,
  } = useChartViewport({
    length: timeline.length,
    defaultWindow,
    minWindow: 8,
    minHeight: 360,
    maxHeight: 520,
    heightRatio: 0.52,
    resetKey,
    pointRatios,
  })

  useCanvasSize(canvasRef, size)
  useCanvasFrame(
    canvasRef,
    size,
    (ctx) =>
      renderSeriesChart({
        ctx,
        width: size.width,
        height: size.height,
        data,
        visibleKeys,
        start: viewport.start,
        end: viewport.end,
        hoverIndex,
        hoverY,
        timeline,
        theme,
      }),
    [data, hoverIndex, hoverY, theme, timeline, viewport, visibleKeys],
  )

  const hoveredTime =
    hoverIndex !== null && hoverIndex >= 0 && hoverIndex < timeline.length ? timeline[hoverIndex] : null

  const valueLookup = useMemo(
    () => ({
      series: data.series.map((item) => ({
        item,
        values: new Map(item.points),
        times: item.points.map(([time]) => time).sort(),
      })),
      bands: (data.bands ?? []).map((band) => ({ band, low: new Map(band.low), high: new Map(band.high) })),
    }),
    [data],
  )

  const unitFor = (item: SeriesDefinition): string | null | undefined =>
    item.pane === "lower" ? data.lower_unit : data.unit

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative min-h-[360px] w-full touch-pan-y overflow-hidden overscroll-none rounded-b-2xl",
        theme.classes.surface,
      )}
    >
      <canvas
        ref={canvasRef}
        className="block cursor-crosshair touch-pan-y"
        aria-label={`${data.title} chart`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerLeave}
      />
      {hoveredTime && (
        <ChartTooltip theme={theme} x={hoverX} width={size.width}>
          <span className={theme.classes.tooltipStrong}>{formatTimeLabel(hoveredTime, data.time_format)}</span>
          {valueLookup.series
            .filter(({ item }) => visibleKeys.includes(item.key))
            .map(({ item, values, times }) => {
              // A sparser series (weekly COT on a daily price timeline) shows its latest
              // reading on or before the hovered time, labelled with that reading's date.
              const asOf = values.has(hoveredTime) ? hoveredTime : latestOnOrBefore(times, hoveredTime)
              const value = asOf === null ? null : (values.get(asOf) ?? null)
              return (
                <Fragment key={item.key}>
                  <span className="inline-flex items-center gap-1">
                    <Swatch color={item.color} />
                    {item.label} {value === null ? "—" : withUnit(formatNumber(value, { compact: false }), unitFor(item))}
                  </span>
                  {value !== null && asOf !== hoveredTime && (
                    <span className={cn("-ml-2", theme.classes.hint)}>as of {formatTimeLabel(asOf!, data.time_format)}</span>
                  )}
                </Fragment>
              )
            })}
          {valueLookup.bands.map(({ band, low, high }) => {
            const lowValue = low.get(hoveredTime) ?? null
            const highValue = high.get(hoveredTime) ?? null
            if (lowValue === null || highValue === null) return null
            return (
              <span key={band.key}>
                {band.label} {formatNumber(lowValue, { compact: false })}–
                {withUnit(formatNumber(highValue, { compact: false }), data.unit)}
              </span>
            )
          })}
        </ChartTooltip>
      )}
      <div className={cn("px-4 pb-2 text-[10px]", theme.classes.hint)}>
        {CHART_NAVIGATION_HINT}
      </div>
    </div>
  )
}

/** Latest time in sorted `times` that is on or before `time` (ISO strings sort chronologically). */
export function latestOnOrBefore(times: string[], time: string): string | null {
  let low = 0
  let high = times.length - 1
  let found: string | null = null
  while (low <= high) {
    const mid = (low + high) >> 1
    if (times[mid] <= time) {
      found = times[mid]
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return found
}
